import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { suspenseOptions } from "@/lib/suspense-query";
import { cn } from "@/lib/utils";

import { Plus, Trash2, Search } from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import ConfirmModal from "@/components/modals/confirm-modal";
import ProductBrandForm from "./-product-brand-form";
import {
  useDeleteProductBrand,
  getGetProductBrandsQueryOptions,
} from "@/requests/gen/react-query/product-brand";
import { Header } from "@/components/universal/header";
import { useQueryState, parseAsString, parseAsInteger } from "nuqs";

export const Route = createFileRoute("/_private/product/brands")({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(getGetProductBrandsQueryOptions()),
  component: ProductBrandsPage,
});

function ProductBrandsPage() {
  const [search, setSearch] = useQueryState(
    "search",
    parseAsString.withDefault(""),
  );
  const [productBrandId, setProductBrandId] = useQueryState(
    "product_brand_id",
    parseAsInteger.withOptions({ history: "push" }),
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const productBrands = useSuspenseQuery(
    suspenseOptions(getGetProductBrandsQueryOptions()),
  );

  // Data is searched & filtered locally when typed
  const filteredProductBrands = productBrands.data?.filter((product_brand) =>
    product_brand.name.toLowerCase().includes(search.toLowerCase()),
  );
  const selectedProductBrand = productBrands.data?.find(
    (brand) => brand.product_brand_id === productBrandId,
  );

  // Delete mutation
  const deleteMutation = useDeleteProductBrand();
  function onDeleteBrand() {
    if (!productBrandId) {
      return;
    } else {
      deleteMutation.mutate(
        { productBrandId },
        {
          onSuccess: () => {
            productBrands.refetch();
            setConfirmDialogOpen(false);
            setProductBrandId(null);
          },
        },
      );
    }
  }

  return (
    <div>
      <title>Product Brands - Legrande Health Portal</title>
      {/* Confirm Delete Dialog */}
      <ConfirmModal
        isOpen={confirmDialogOpen}
        text={`Are you sure you want to delete "${selectedProductBrand?.name}" product brand?`}
        title={`Delete ${selectedProductBrand?.name}`}
        onCancel={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          onDeleteBrand();
        }}
        img="delete"
        cancelText="Cancel"
        confirmText="Delete"
      />
      <Header
        crumbs={[{ name: "Product Brands", link: { to: "/product/brands" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Product Brands</CardTitle>
          <CardDescription>
            Manage product brands. Search, edit, and create all on this page.
          </CardDescription>
          <CardAction>
            <Button size="sm" onClick={() => setProductBrandId(0)}>
              <Plus />
              New Brand
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Separator className="mb-4" />
          <div className="flex">
            {/* Left Navigation */}
            <div
              className={cn(
                "w-full md:w-1/4 md:border-r md:pr-3 space-y-2",
                productBrandId !== null && "hidden md:block",
              )}
            >
              {/* Search bar */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  placeholder="Search"
                  className="pl-8"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                />
              </div>

              {/* Product Brand list */}
              <ScrollArea>
                <ul className="flex flex-col h-96 space-y-1">
                  {filteredProductBrands?.map((product_brand) => {
                    return (
                      <li
                        key={product_brand.product_brand_id}
                        className={cn(
                          "rounded-md text-sm font-medium cursor-pointer px-3 py-2 transition-colors hover:bg-accent/50",
                          selectedProductBrand?.product_brand_id ===
                            product_brand.product_brand_id
                            ? "bg-accent text-accent-foreground font-semibold"
                            : "text-foreground",
                        )}
                        onClick={() => {
                          setProductBrandId(product_brand.product_brand_id);
                        }}
                      >
                        {product_brand.name}
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </div>

            {/* Create/edit brand form */}
            <div
              className={cn(
                "w-full md:w-3/4 py-3 md:pl-5",
                productBrandId === null && "hidden md:block",
              )}
            >
              <div className="flex flex-row justify-between items-center">
                <h4 className="text-xl font-semibold leading-none tracking-tight h-6">
                  {selectedProductBrand
                    ? "Update Product Brand"
                    : "New Product Brand"}
                </h4>
                {selectedProductBrand && (
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip="Delete"
                    onClick={() => setConfirmDialogOpen(true)}
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>
              <div className="flex items-center h-full">
                <ProductBrandForm
                  key={selectedProductBrand?.product_brand_id}
                  productBrand={selectedProductBrand ?? undefined}
                  onSuccessFn={() => {
                    productBrands.refetch();
                    setProductBrandId(null);
                  }}
                  onCancelBtn={
                    <Button
                      variant="outline"
                      onClick={() => setProductBrandId(null)}
                    >
                      Cancel
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
